(() => {
  if (window.featuredCollectionMobileSwipeLoaded) return;
  window.featuredCollectionMobileSwipeLoaded = true;

  const mobileQuery = window.matchMedia('(max-width: 749px)');
  const mediaSelector = '.featured-collection-slider .media--mobile-swipe';
  const swipeThreshold = 28;

  const syncIndicator = (media) => {
    const indicator = media.parentElement?.querySelector('.card__media-swipe-indicator');
    if (!indicator) return;

    const activeIndex = Number(media.dataset.activeImage || 0);
    indicator.querySelectorAll('button').forEach((item, index) => {
      item.classList.toggle('is-active', index === activeIndex);
      item.toggleAttribute('aria-current', index === activeIndex);
    });
  };

  const setImageTransforms = (media, activeIndex, offset = 0, smooth = true) => {
    const width = media.clientWidth || media.getBoundingClientRect().width;

    media.querySelectorAll('img').forEach((image, index) => {
      image.style.transition = smooth ? 'transform 220ms ease' : 'none';

      if (offset === 0) {
        image.style.transform = `translateX(${(index - activeIndex) * 100}%)`;
        return;
      }

      image.style.transform = `translateX(${(index - activeIndex) * width + offset}px)`;
    });
  };

  const clampSwipeOffset = (media, activeIndex, offset) => {
    const width = media.clientWidth || media.getBoundingClientRect().width;
    const imageCount = media.querySelectorAll('img').length;
    const minOffset = -(imageCount - 1 - activeIndex) * width;
    const maxOffset = activeIndex * width;

    return Math.min(maxOffset, Math.max(minOffset, offset));
  };

  const setActiveImage = (media, index, smooth = true) => {
    const imageCount = media.querySelectorAll('img').length;
    const activeIndex = Math.max(0, Math.min(index, imageCount - 1));

    media.dataset.activeImage = activeIndex.toString();
    setImageTransforms(media, activeIndex, 0, smooth);
    syncIndicator(media);
  };

  const resetDesktopImages = (media) => {
    delete media.dataset.activeImage;
    media.querySelectorAll('img').forEach((image) => {
      image.style.removeProperty('transform');
      image.style.removeProperty('transition');
    });
    syncIndicator(media);
  };

  const applyCurrentMode = (media) => {
    if (mobileQuery.matches) {
      setActiveImage(media, Number(media.dataset.activeImage || 0), false);
      return;
    }

    resetDesktopImages(media);
  };

  const initMedia = (media) => {
    if (media.dataset.mobileSwipeReady === 'true') return;
    media.dataset.mobileSwipeReady = 'true';

    let startX = 0;
    let startY = 0;
    let startIndex = 0;
    let outerStartScrollLeft = 0;
    let moved = false;
    let tracking = false;

    const outerSlider = media.closest('.featured-collection__mobile-slider');

    const getPoint = (event) => {
      const point = event.changedTouches?.[0] || event.touches?.[0] || event;
      if (typeof point.clientX !== 'number' || typeof point.clientY !== 'number') return null;
      return point;
    };

    const stopImageSwipeEvent = (event) => {
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    const unlockOuterSlider = () => {
      outerSlider?.classList.remove('featured-collection__mobile-slider--image-swiping');
    };

    const beginSwipe = (event) => {
      if (!mobileQuery.matches || tracking) return;

      const point = getPoint(event);
      if (!point) return;

      startX = point.clientX;
      startY = point.clientY;
      startIndex = Number(media.dataset.activeImage || 0);
      outerStartScrollLeft = outerSlider?.scrollLeft || 0;
      moved = false;
      tracking = true;
      outerSlider?.classList.add('featured-collection__mobile-slider--image-swiping');
      if (event.pointerId != null) media.setPointerCapture?.(event.pointerId);
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    };

    const moveSwipe = (event) => {
      if (!tracking || !mobileQuery.matches) return;

      const point = getPoint(event);
      if (!point) return;

      const deltaX = point.clientX - startX;
      const deltaY = point.clientY - startY;

      if (Math.abs(deltaX) <= 8 || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      moved = true;
      if (outerSlider) outerSlider.scrollLeft = outerStartScrollLeft;
      setImageTransforms(media, startIndex, clampSwipeOffset(media, startIndex, deltaX), false);
      stopImageSwipeEvent(event);
    };

    const endSwipe = (event) => {
      if (!tracking || !mobileQuery.matches) return;

      const point = getPoint(event);
      tracking = false;
      if (event.pointerId != null) media.releasePointerCapture?.(event.pointerId);

      if (!point) {
        setActiveImage(media, startIndex);
        unlockOuterSlider();
        return;
      }

      const deltaX = point.clientX - startX;
      const deltaY = point.clientY - startY;
      const shouldChange = Math.abs(deltaX) >= swipeThreshold && Math.abs(deltaX) > Math.abs(deltaY);

      setActiveImage(media, shouldChange ? startIndex + (deltaX < 0 ? 1 : -1) : startIndex);

      if (outerSlider) outerSlider.scrollLeft = outerStartScrollLeft;
      window.setTimeout(unlockOuterSlider, 240);

      if (moved) stopImageSwipeEvent(event);
    };

    const cancelSwipe = (event) => {
      if (!tracking) return;
      tracking = false;
      if (event.pointerId != null) media.releasePointerCapture?.(event.pointerId);
      setActiveImage(media, startIndex);
      unlockOuterSlider();
    };

    media.addEventListener('pointerdown', beginSwipe);
    media.addEventListener('pointermove', moveSwipe);
    media.addEventListener('pointerup', endSwipe);
    media.addEventListener('pointercancel', cancelSwipe);
    media.addEventListener('mousedown', beginSwipe);
    media.addEventListener('mousemove', moveSwipe);
    media.addEventListener('mouseup', endSwipe);
    media.addEventListener('mouseleave', cancelSwipe);
    media.addEventListener('touchstart', beginSwipe, { passive: true });
    media.addEventListener('touchmove', moveSwipe, { passive: false });
    media.addEventListener('touchend', endSwipe);
    media.addEventListener('touchcancel', cancelSwipe);

    media.addEventListener('click', (event) => {
      if (!mobileQuery.matches) return;

      if (moved) {
        stopImageSwipeEvent(event);
        return;
      }

      const productLink = media.closest('.card-wrapper')?.querySelector('.card__heading a[href]');
      if (productLink) window.location.href = productLink.href;
    });

    const indicator = media.parentElement?.querySelector('.card__media-swipe-indicator');

    indicator?.addEventListener('pointerdown', (event) => {
      if (!mobileQuery.matches) return;
      event.stopPropagation();
      event.stopImmediatePropagation?.();
    });

    indicator?.addEventListener('touchstart', (event) => {
      if (!mobileQuery.matches) return;
      event.stopPropagation();
    });

    indicator?.querySelectorAll('button').forEach((button) => {
      button.addEventListener('click', (event) => {
        if (!mobileQuery.matches) return;

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation?.();
        setActiveImage(media, Number(button.dataset.imageIndex || 0));
      });
    });

    applyCurrentMode(media);
  };

  const init = (root = document) => {
    root.querySelectorAll(mediaSelector).forEach(initMedia);
  };

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load', (event) => init(event.target));

  mobileQuery.addEventListener('change', () => {
    document.querySelectorAll(mediaSelector).forEach((media) => {
      applyCurrentMode(media);
    });
  });

  window.addEventListener('resize', () => {
    document.querySelectorAll(mediaSelector).forEach((media) => {
      applyCurrentMode(media);
    });
  });
})();
